import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/auth-context";
import {
    ABOUT_PATH,
    ADMIN_PATH,
    FIGHTER_PATH,
    LOGIN_PATH,
    PROFILE_PATH,
} from "../App";
import {
    auth,
    getEvents,
    getRanking,
    statisticsCollection,
} from "../utils/firebase";
import SocialMediaLink from "../components/social-media-link";
import { twMerge } from "tailwind-merge";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { Draggable } from "gsap/Draggable";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { fighterEntryDisplayMapping, weightClassesName } from "../constants";
import { doc, getDoc } from "firebase/firestore";
import RankingCard from "../components/ranking-card";
import { EventCard } from "../components/event-card";
import LanguageSwitcher from "../components/language-switcher";
import { useTranslation } from "react-i18next";

export default function Landing() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { currentUser } = useAuth();

    const landingContainer = useRef(null);
    const heroSection = useRef(null);
    const heroFighterImg = useRef(null);
    const eventContainer = useRef(null);
    const rankingContainer = useRef(null);

    const [rankings, setRankings] = useState(null);
    const [rankingFighters, setRankingFighter] = useState(null);

    const [selectedRankingEntry, setSelectedRankingEntry] = useState("BOXING");
    const [selectedRankingWeightClass, setSelectedRankingWeightClass] =
        useState("C");
    const [isFilterDisabled, setIsFilterDisabled] = useState(false);

    const [upcomingEvents, setUpComingEvents] = useState(null);
    const [archiveEvents, setArchiveEvents] = useState(null);

    const [stats, setStats] = useState(null);

    const hashtags = [
        "malaysia_fight_league",
        "event_organiser",
        "boxing",
        "muay_thai",
        "mma",
        "competition",
        "combar_sports",
        "kick_boxing",
        "mfl",
    ];

    gsap.registerPlugin(Draggable, ScrollTrigger);

    useGSAP(
        () => {
            // HERO SECTION START
            gsap.from(heroFighterImg?.current, {
                x: "120%",
                scale: 0.5,
                ease: "ease.inOut",
                duration: 1,
                delay: 1,
            });

            gsap.from(".hero-text-1, .hero-text-3", {
                x: "120%",
                ease: "power3.out",
                duration: 2,
            });
            gsap.from(".hero-text-2, .hero-text-4", {
                x: "-120%",
                ease: "power3.out",
                duration: 2,
            });
            gsap.fromTo(
                ".hero-text-4",
                {
                    color: "#000",
                },
                {
                    color: "#fff",
                    ease: "power2.inOut",
                    duration: 1,
                    delay: 2,
                }
            );

            gsap.from("#punch", {
                scale: 0,
                delay: 1,
            });

            gsap.from(".hero-desc", {
                x: "-100%",
                delay: 1.5,
            });

            const navItems = gsap.utils.toArray(".nav-item");

            gsap.from(navItems, {
                y: "-120%",
                duration: 0.5,
            });

            gsap.from(".about-us", {
                y: "120%",
                duration: 0.5,
            });

            const socialMediaLinks = gsap.utils.toArray(".social-media-link");
            gsap.from(socialMediaLinks, {
                y: "120%",
                stagger: 0.25,
                duration: 0.5,
            });

            gsap.from(".header-events", {
                x: "-120%",
                delay: 0.25,
                stagger: 0.25,
                scrollTrigger: {
                    start: "start bottom",
                    trigger: eventContainer?.current,
                },
            });
            gsap.from(".header-rankings", {
                x: "-120%",
                delay: 0.25,
                stagger: 0.25,
                scrollTrigger: {
                    start: "center bottom",
                    trigger: rankingContainer?.current,
                },
            });

            const filterButtonContainer = gsap.utils.toArray(
                ".filter-button-container"
            );
            gsap.from(filterButtonContainer, {
                opacity: 0,
                delay: 0.25,
                stagger: 0.5,
                scrollTrigger: {
                    start: "center bottom",
                    trigger: rankingContainer?.current,
                },
            });

            const hashtagElements = gsap.utils.toArray(".hashtag");

            const tl = gsap.timeline({
                repeat: -1,
                defaults: {
                    ease: "none",
                },
                reversed: false,
                onReverseComplete: () =>
                    tl.totalTime(tl.rawTime() + tl.duration() * 100),
            });

            const length = hashtagElements.length;
            const startX = hashtagElements[0].offsetLeft;
            const times = [];
            const widths = [];
            const xPercents = [];
            let curIndex = 0;
            let pixelsPerSecond = 1 * 100;
            const snap = (v) => v;
            const populateWidths = () => {
                hashtagElements.forEach((el, i) => {
                    widths[i] = parseFloat(gsap.getProperty(el, "width", "px"));
                    xPercents[i] = snap(
                        (parseFloat(gsap.getProperty(el, "x", "px")) /
                            widths[i]) *
                        100 +
                        gsap.getProperty(el, "xPercent")
                    );
                });
            };
            const getTotalWidth = () => {
                return (
                    hashtagElements[length - 1].offsetLeft +
                    (xPercents[length - 1] / 100) * widths[length - 1] -
                    startX +
                    hashtagElements[length - 1].offsetWidth *
                    gsap.getProperty(hashtagElements[length - 1], "scaleX")
                );
            };
            populateWidths();
            let totalWidth = getTotalWidth();
            let curX, distanceToStart, distanceToLoop;

            gsap.set(hashtagElements, { x: 0 });

            gsap.from(hashtagElements, { opacity: 0, delay: 2.5 });

            for (let i = 0; i < length; i++) {
                const hashtagElement = hashtagElements[i];
                curX = (xPercents[i] / 100) * widths[i];
                distanceToStart = hashtagElement.offsetLeft + curX - startX;
                distanceToLoop =
                    distanceToStart +
                    widths[i] * gsap.getProperty(hashtagElement, "scaleX");
                tl.to(
                    hashtagElement,
                    {
                        xPercent: snap(
                            ((curX - distanceToLoop) / widths[i]) * 100
                        ),
                        duration: distanceToLoop / pixelsPerSecond,
                    },
                    0
                )
                    .fromTo(
                        hashtagElement,
                        {
                            xPercent: snap(
                                ((curX - distanceToLoop + totalWidth) /
                                    widths[i]) *
                                100
                            ),
                        },
                        {
                            xPercent: xPercents[i],
                            duration:
                                (curX - distanceToLoop + totalWidth - curX) /
                                pixelsPerSecond,
                            immediateRender: false,
                        },
                        distanceToLoop / pixelsPerSecond
                    )
                    .add("label" + i, distanceToStart / pixelsPerSecond);
                times[i] = distanceToStart / pixelsPerSecond;
            }

            function toIndex(index, vars) {
                vars = vars || {};
                Math.abs(index - curIndex) > length / 2 &&
                    (index += index > curIndex ? -length : length); // always go in the shortest direction
                let newIndex = gsap.utils.wrap(0, length, index),
                    time = times[newIndex];
                if (time > tl.time() !== index > curIndex) {
                    // if we're wrapping the timeline's playhead, make the proper adjustments
                    vars.modifiers = {
                        time: gsap.utils.wrap(0, tl.duration()),
                    };
                    time += tl.duration() * (index > curIndex ? 1 : -1);
                }
                curIndex = newIndex;
                vars.overwrite = true;
                return tl.tweenTo(time, vars);
            }

            tl.next = (vars) => toIndex(curIndex + 1, vars);
            tl.previous = (vars) => toIndex(curIndex - 1, vars);
            tl.current = () => curIndex;
            tl.toIndex = (index, vars) => toIndex(index, vars);
            tl.updateIndex = () =>
            (curIndex = Math.round(
                tl.progress() * (hashtagElements.length - 1)
            ));
            tl.times = times;
            tl.progress(1, true).progress(0, true); // pre-render for performance
            if (true) {
                tl.vars.onReverseComplete();
                tl.reverse();
            }
            if (typeof Draggable === "function") {
                let proxy = document.createElement("div");

                const wrap = gsap.utils.wrap(0, 1);

                const align = () =>
                    tl.progress(
                        wrap(
                            startProgress +
                            (draggable.startX - draggable.x) * ratio
                        )
                    );
                const syncIndex = () => tl.updateIndex();

                let ratio, startProgress, draggable, dragSnap, roundFactor;

                draggable = Draggable.create(proxy, {
                    trigger: hashtagElements[0].parentNode,
                    type: "x",
                    onPress() {
                        startProgress = tl.progress();
                        tl.progress(0);
                        populateWidths();
                        totalWidth = getTotalWidth();
                        ratio = 1 / totalWidth;
                        dragSnap = totalWidth / hashtagElements.length;
                        roundFactor = Math.pow(
                            10,
                            ((dragSnap + "").split(".")[1] || "").length
                        );
                        tl.progress(startProgress);
                    },
                    onDrag: align,
                    onThrowUpdate: align,
                    inertia: true,
                    snap: (value) => {
                        let n =
                            Math.round(parseFloat(value) / dragSnap) *
                            dragSnap *
                            roundFactor;
                        return (n - (n % 1)) / roundFactor;
                    },
                    onRelease: syncIndex,
                    onThrowComplete: () =>
                        gsap.set(proxy, { x: 0 }) && syncIndex(),
                })[0];
            }

            hashtagElements.forEach((el, index) => {
                el.addEventListener("mouseenter", () =>
                    gsap.to(tl, { timeScale: 0, overwrite: true })
                );
                el.addEventListener("mouseleave", () =>
                    gsap.to(tl, { timeScale: 1, overwrite: true })
                );
            });
        },
        {
            scope: landingContainer,
        }
    );

    useGSAP(
        () => {
            // EVENTS SECTION START
            const eventPhotoContainers = gsap.utils.toArray(
                ".event-photo-container"
            );
            const eventPhotos = gsap.utils.toArray(".event-photo");
            const eventTypes = gsap.utils.toArray(".event-type");
            const eventTitles = gsap.utils.toArray(".event-title");
            if (eventPhotoContainers.length > 0) {
                gsap.from(eventPhotoContainers, {
                    borderColor: "#000",
                    stagger: 0.25,
                    scrollTrigger: {
                        start: "center bottom",
                        trigger: eventContainer?.current,
                    },
                });
            }
            if (eventPhotos.length > 0) {
                gsap.from(eventPhotos, {
                    x: "120%",
                    stagger: 0.25,
                    scrollTrigger: {
                        start: "center bottom",
                        trigger: eventContainer?.current,
                    },
                });
            }
            if (eventTypes.length > 0) {
                gsap.from(eventTypes, {
                    x: "120%",
                    delay: 0.25,
                    stagger: 0.25,
                    scrollTrigger: {
                        start: "center bottom",
                        trigger: eventContainer?.current,
                    },
                });
            }
            if (eventTitles.length > 0) {
                gsap.from(eventTitles, {
                    x: "-120%",
                    delay: 0.25,
                    stagger: 0.25,
                    scrollTrigger: {
                        start: "center bottom",
                        trigger: eventContainer?.current,
                    },
                });
            }
        },
        {
            scope: landingContainer,
            dependencies: [upcomingEvents, archiveEvents],
        }
    );

    useGSAP(
        () => {
            // RANKINGS SECTION START
            const rankingCards = gsap.utils.toArray(".ranking-card");
            if (rankingCards.length > 0) {
                gsap.from(rankingCards, {
                    y: "120%",
                    stagger: 0.25,
                    scrollTrigger: {
                        start: "center bottom",
                        trigger: rankingContainer?.current,
                    },
                });
            }
        },
        {
            scope: landingContainer,
            dependencies: [rankingFighters],
        }
    );

    useGSAP(
        () => {
            gsap.fromTo(
                "#stats-events",
                {
                    innerText: 0,
                },
                {
                    innerText: stats?.events ?? 0,
                    duration: 2.5,
                    snap: {
                        innerText: 1,
                    },
                    overwrite: true,
                    scrollTrigger: {
                        trigger: eventContainer?.current,
                        start: "center center",
                    },
                }
            );
            gsap.fromTo(
                "#stats-fighters",
                {
                    innerText: 0,
                },
                {
                    innerText: stats?.fighters ?? 0,
                    duration: 3,
                    snap: {
                        innerText: 1,
                    },
                    overwrite: true,
                    scrollTrigger: {
                        trigger: eventContainer?.current,
                        start: "center center",
                    },
                }
            );
            gsap.fromTo(
                "#stats-attendees",
                {
                    innerText: 0,
                },
                {
                    innerText: stats?.attendees ?? 0,
                    duration: 2,
                    snap: {
                        innerText: 1,
                    },
                    overwrite: true,
                    scrollTrigger: {
                        trigger: eventContainer?.current,
                        start: "center center",
                    },
                }
            );
        },
        {
            dependencies: [stats],
            scope: landingContainer?.current,
        }
    );

    useEffect(() => {
        (async () => {
            const fetchedRankings = await getRanking();
            setRankings(fetchedRankings ?? {});
            const statisticSnapshot = await getDoc(
                doc(statisticsCollection, "home_page")
            );
            if (statisticSnapshot.exists()) {
                setStats({
                    ...statisticSnapshot.data(),
                });
            }

            const fetchEvents = await getEvents();

            fetchEvents.sort((a, b) => {
                return b?.dateTime?.toDate() - a?.dateTime?.toDate();
            });

            const now = new Date();
            setUpComingEvents((prev) => {
                return (
                    fetchEvents?.filter((e) => e?.dateTime?.toDate() >= now) ??
                    []
                );
            });
            setArchiveEvents((prev) => {
                return (
                    fetchEvents?.filter((e) => e?.dateTime?.toDate() < now) ??
                    []
                );
            });
        })();
    }, []);

    useEffect(() => {
        const fighters =
            rankings?.[selectedRankingWeightClass]?.[selectedRankingEntry] ??
            [];
        // Filter out fighters with 0-0-0 record for the selected entry
        const filteredFighters = fighters.filter((fighter) => {
            const entryRecord = fighter?.records?.[selectedRankingEntry];
            if (!entryRecord) return false;
            
            const win = parseInt(entryRecord.win) || 0;
            const loss = parseInt(entryRecord.loss) || 0;
            const draw = parseInt(entryRecord.draw) || 0;
            
            // Exclude fighters with 0-0-0 record
            return (win + loss + draw) > 0;
        });


        // Sort fighters by metric in descending order (highest metric first)
        const sortedFighters = [...filteredFighters].sort((a, b) => {
            const metricA = a.metric ?? 0;
            const metricB = b.metric ?? 0;
            return metricB - metricA; // Descending order
        });

        setRankingFighter(sortedFighters);
    }, [rankings, selectedRankingEntry, selectedRankingWeightClass]);

    const handleFilterClick = (setter, value) => {
        if (isFilterDisabled) return;
        setIsFilterDisabled(true);
        setter(value);
        setTimeout(() => {
            setIsFilterDisabled(false);
        }, 1000);
    };

    return (
        <>
            <div
                className="flex-center justify-start min-h-screen w-full overflow-x-hidden"
                ref={landingContainer}
            >
                <nav
                    className={twMerge(
                        "fixed top-0 left-0 z-[999] flex flex-row justify-between md:justify-around items-center px-4 gap-4 w-full",
                        "bg-gray-400 bg-clip-padding backdrop-filter backdrop-blur-lg bg-opacity-10 border-b-2 border-gray-100"
                    )}
                >
                    <div className="overflow-hidden flex-1 flex-center hidden md:flex">
                        <a
                            href="#home"
                            className="nav-item text-center uppercase font-bold"
                        >
                            {t("landing.home")}
                        </a>
                    </div>

                    <div className="overflow-hidden flex-1 flex-center hidden md:flex">
                        <a
                            href="#rankings"
                            className="nav-item text-center uppercase font-bold"
                        >
                            {t("landing.rankings")}
                        </a>
                    </div>

                    <div className="overflow-hidden md:flex-1 flex-center">
                        <a
                            href="#home"
                            className="nav-item flex-center text-center"
                        >
                            <img
                                className="h-14 py-2 object-contain object-center"
                                src="/assets/img/logo-mfl.png"
                                alt="logo"
                            />
                        </a>
                    </div>
                    <div className="flex-1 md:hidden" />

                    <div className="overflow-hidden flex-1 flex-center hidden md:flex">
                        <a
                            href="#events"
                            className="nav-item text-center uppercase font-bold"
                        >
                            {t("landing.events")}
                        </a>
                    </div>

                    <div className="md:flex-1 flex-center overflow-hidden">
                        {!currentUser ? (
                            <button
                                className="nav-item text-center uppercase font-bold underline"
                                onClick={() => navigate(LOGIN_PATH)}
                            >
                                {t("landing.login")}
                            </button>
                        ) : currentUser.role?.toUpperCase() === "ADMIN" ? (
                            <button
                                className="nav-item text-center uppercase font-bold underline"
                                onClick={() => navigate(ADMIN_PATH)}
                            >
                                {t("landing.dashboard")}
                            </button>
                        ) : (
                            <button
                                className="nav-item text-center uppercase font-bold underline"
                                onClick={() => navigate(PROFILE_PATH)}
                            >
                                {t("landing.profile")}
                            </button>
                        )}
                    </div>
                </nav>
                <main className="w-full">
                    {/* Hero Section */}
                    <div
                        className="flex-center page-size min-h-[calc(100vh-7rem)] max-[680px]:min-h-[10rem]  relative gap-4"
                        ref={heroSection}
                    >
                        <div className="flex flex-col justify-around items-start gap-4 h-full flex-1 w-full px-6 max-[500px]:px-2 relative max-[1080px]:justify-start max-[1080px]:gap-0 max-[680px]:justify-end">
                            <div className="flex gap-4 items-start justify-start overflow-hidden">
                                <p className="hero-text hero-text-1 text-[12rem] font-bold strokeme leading-[.82]  text-nowrap max-[1080px]:text-[8rem] max-[680px]:text-[6.6rem] max-[500px]:text-[5rem] max-[380px]:text-[4.2rem] max-[680px]:mt-2 tracking-[.5rem] max-[1080px]:leading-[1]">
                                    {t("landing.hero.areYou")}{" "}
                                    <span className="hidden max-[1080px]:block" />{" "}
                                    {t("landing.hero.you")}
                                </p>
                                <img
                                    className="block max-[500px]:hidden select-none"
                                    id="punch"
                                    src="/assets/img/punch.png"
                                    alt=""
                                />
                            </div>
                            <div className="overflow-hidden">
                                <p className="hero-text hero-text-2 text-[12rem] font-bold strokeme leading-[.82]  text-nowrap max-[1080px]:text-[8rem] max-[680px]:text-[6.6rem] max-[500px]:text-[5rem] max-[380px]:text-[4.2rem] max-[680px]:mt-2 tracking-[.5rem] max-[1080px]:leading-[1]">
                                    {t("landing.hero.ready")}
                                </p>
                            </div>
                            <div className="overflow-hidden">
                                <p className="hero-text hero-text-3 text-[12rem] font-bold strokeme leading-[.82]  text-nowrap max-[1080px]:text-[8rem] max-[680px]:text-[6.6rem] max-[500px]:text-[5rem] max-[380px]:text-[4.2rem] max-[680px]:mt-2 tracking-[.5rem] max-[1080px]:leading-[1]">
                                    {t("landing.hero.toJoin")}{" "}
                                    <span className="inline max-[1080px]:hidden">
                                        {t("landing.hero.a")}
                                    </span>
                                </p>
                            </div>
                            <div className="flex gap-4 items-start justify-start max-[1080px]:flex-col">
                                <div className="overflow-hidden">
                                    <p className="hero-text hero-text-4 text-[12rem] font-bold strokeme leading-[.82]  text-nowrap max-[1080px]:text-[8rem] max-[680px]:text-[6.6rem] max-[500px]:text-[5rem] max-[380px]:text-[4.2rem] max-[680px]:mt-2 tracking-[.5rem] max-[1080px]:leading-[1] text-white text-nowrap">
                                        <span className="inline min-[1080px]:hidden !text-black">
                                            {t("landing.hero.a")}{" "}
                                        </span>{" "}
                                        {t("landing.hero.fight")}
                                    </p>
                                </div>
                                <div className="w-80 overflow-hidden max-[680px]:w-full max-w-screen">
                                    <p className="hero-desc w-full max-[680px]:w-full relative z-[2]">
                                        {t("landing.hero.description")}
                                    </p>
                                </div>
                            </div>
                            {/* Image */}
                            <img
                                ref={heroFighterImg}
                                className="scale-110 absolute h-full top-0 right-4 object-contain object-left max-[1080px]:h-[90%] max-[820px]:h-[60%] max-[500px]:h-[45%] select-none"
                                src="/assets/img/hero-fighter.png"
                                alt="fighter_image"
                            />
                        </div>
                        <div className="flex flex-row items-center justify-center gap-4 max-w-[100vw] overflow-x-hidden">
                            {hashtags.map((hashtag, index) => {
                                return (
                                    <p key={index} className="hashtag">
                                        #{hashtag}
                                    </p>
                                );
                            })}
                            {hashtags.map((hashtag, index) => {
                                return (
                                    <p key={index} className="hashtag">
                                        #{hashtag}
                                    </p>
                                );
                            })}
                        </div>
                    </div>
                    <div className="h-24 max-[500px]:h-12" id="events" />
                    {/* Events Section */}
                    <div className="page-size min-h-0 flex-center gap-8">
                        <p className="text-[5em] self-start font-bold border-l-[.8rem] pl-4 max-[500px]:ml-2 ml-6 leading-[.82] header-events max-[680px]:text-[3rem]">
                            {t("landing.events")}
                        </p>
                        <div
                            ref={eventContainer}
                            className="flex flex-row justify-start items-center gap-8 w-[calc(100%-2rem)] overflow-x-auto max-[500px]:w-[calc(100%-1rem)]"
                        >
                            {upcomingEvents?.length === 0 &&
                                archiveEvents?.length === 0 ? (
                                <div className="w-full min-h-[50vh] flex-center opacity-80">
                                    {t("landing.noEvent")}
                                </div>
                            ) : null}
                            {!!upcomingEvents ? (
                                upcomingEvents.map((event, index) => {
                                    return (
                                        <EventCard
                                            key={index}
                                            index={index}
                                            event={event}
                                            type="UPCOMING"
                                        />
                                    );
                                })
                            ) : (
                                <div className="w-full min-h-[50vh] flex-center">
                                    <div className="loading loading-bars" />
                                </div>
                            )}
                            {!!archiveEvents ? (
                                archiveEvents.map((event, index) => {
                                    return (
                                        <EventCard
                                            key={index}
                                            index={index}
                                            event={event}
                                            type="ARCHIVE"
                                        />
                                    );
                                })
                            ) : (
                                <div className="w-full min-h-[50vh] flex-center">
                                    <div className="loading loading-bars" />
                                </div>
                            )}
                        </div>
                        <div className="self-end stats stats-vertical lg:stats-horizontal shadow flex flex-row justify-end items-center gap-4">
                            <div className="stat min-w-32 max-[500px]:min-w-[0px] max-[500px]:p-2  w-auto flex flex-col justify-center items-end">
                                <div className="stat-value" id="stats-events">
                                    0
                                </div>
                                <div className="stat-title">
                                    {t("landing.stats.events")}
                                </div>
                            </div>

                            <div className="stat min-w-32 max-[500px]:min-w-[0px] max-[500px]:p-2  w-auto flex flex-col justify-center items-end">
                                <div className="stat-value" id="stats-fighters">
                                    0
                                </div>
                                <div className="stat-title">
                                    {t("landing.stats.fighters")}
                                </div>
                            </div>

                            <div className="stat min-w-32 max-[500px]:min-w-[0px] max-[500px]:p-2  w-auto flex flex-col justify-center items-end">
                                <div className="stat-value">
                                    <span id="stats-attendees">0</span>
                                </div>
                                <div className="stat-title">
                                    {t("landing.stats.attendees")}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="h-24" id="rankings" />
                    <div className="page-size min-h-0 flex-center gap-4 p-4 max-[500px]:p-2 justify-start max-[500px]:min-h-[0px] max-[500px]:mb-12 mb-12">
                        <p className="text-[5em] self-start text-start font-bold border-l-[.8rem] pl-4 max-[500px]:ml-0 ml-2 leading-[.82] header-rankings max-[680px]:text-[3rem]">
                            {t("landing.rankings")}
                        </p>
                        <fieldset className="fieldset w-full flex gap-2 flex-col md:hidden">
                            <legend className="fieldset-legend mb-2">
                                Entry
                            </legend>
                            <select
                                name=""
                                id=""
                                className="select w-full !border border-current select-sm"
                                onChange={(e) => {
                                    handleFilterClick(
                                        setSelectedRankingEntry,
                                        e.target.key
                                    );
                                }}
                                disabled={isFilterDisabled}
                                value={selectedRankingEntry}
                            >
                                {Object.entries(fighterEntryDisplayMapping).map(
                                    ([key, value], index) => {
                                        return (
                                            <option value={key} key={index}>
                                                {value}
                                            </option>
                                        );
                                    }
                                )}
                            </select>
                        </fieldset>

                        <fieldset className="fieldset w-full flex gap-2 flex-col md:hidden">
                            <legend className="fieldset-legend mb-2">
                                Weight Class
                            </legend>
                            <select
                                name=""
                                id=""
                                className="select w-full !border border-current select-sm"

                                onChange={(e) => {
                                    handleFilterClick(
                                        setSelectedRankingWeightClass,
                                        e.target.value
                                    );
                                }}
                                disabled={isFilterDisabled}
                                value={selectedRankingWeightClass}
                            >
                                {Object.entries(weightClassesName).map(
                                    ([key, value], index) => {
                                        return (
                                            <option value={key} key={index}>
                                                {value}
                                            </option>
                                        );
                                    }
                                )}
                            </select>
                        </fieldset>

                        <div className="relative w-full hidden md:block">
                            <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-base-100 to-transparent z-10 pointer-events-none"></div>
                            <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-base-100 to-transparent z-10 pointer-events-none"></div>

                            <div className="flex flex-row items-center justify-end gap-4 w-full overflow-x-auto px-2 overflow-y-hidden filter-button-container">
                                {Object.entries(fighterEntryDisplayMapping).map(
                                    ([key, value], index) => {
                                        return (
                                            <button
                                                key={index}
                                                className={twMerge(
                                                    "btn btn-ghost btn-sm",
                                                    selectedRankingEntry === key
                                                        ? "btn-primary"
                                                        : "",
                                                    isFilterDisabled
                                                        ? "opacity-50 cursor-not-allowed"
                                                        : ""
                                                )}
                                                onClick={() =>
                                                    handleFilterClick(
                                                        setSelectedRankingEntry,
                                                        key
                                                    )
                                                }
                                                disabled={isFilterDisabled}
                                            >
                                                {`${value}`}
                                            </button>
                                        );
                                    }
                                )}
                            </div>
                        </div>
                        <div className="relative w-full hidden md:block">
                            <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-base-100 to-transparent z-10 pointer-events-none"></div>
                            <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-base-100 to-transparent z-10 pointer-events-none"></div>
                            <div className="flex flex-row items-center justify-start gap-4 w-full overflow-x-auto px-2 overflow-y-hidden pb-2 filter-button-container">
                                {Object.entries(weightClassesName).map(
                                    ([key, value], index) => {
                                        return (
                                            <button
                                                key={index}
                                                className={twMerge(
                                                    "btn btn-ghost btn-sm",
                                                    selectedRankingWeightClass ===
                                                        key
                                                        ? "btn-primary"
                                                        : "",
                                                    isFilterDisabled
                                                        ? "opacity-50 cursor-not-allowed"
                                                        : ""
                                                )}
                                                onClick={() =>
                                                    handleFilterClick(
                                                        setSelectedRankingWeightClass,
                                                        key
                                                    )
                                                }
                                                disabled={isFilterDisabled}
                                            >
                                                {`${value}`}
                                            </button>
                                        );
                                    }
                                )}
                            </div>
                        </div>
                        <div
                            ref={rankingContainer}
                            className="flex flex-row justify-start items-center gap-8 w-[calc(100%-2rem)] overflow-x-auto overflow-y-hidden max-[500px]:w-[calc(100%-.5rem)]"
                        >
                            {!!rankingFighters ? (
                                rankingFighters.length > 0 ? (
                                    rankingFighters.map((fighter, index) => (
                                        <Link
                                            key={`${fighter.id}-${index}`}
                                            to={FIGHTER_PATH.replace(
                                                ":id",
                                                fighter?.id ?? ""
                                            )}
                                        >
                                            <div className="oevrflow-hidden">
                                                <RankingCard
                                                    key={index}
                                                    index={index}
                                                    className={
                                                        "ranking-card sticky shadow-lg shadow-black left-0 bg-black"
                                                    }
                                                    fighter={fighter}
                                                    entry={selectedRankingEntry}
                                                />
                                            </div>
                                        </Link>
                                    ))
                                ) : (
                                    <div className="w-full min-h-[10rem] py-24 flex-center">
                                        <img
                                            className="opacity-10"
                                            src="/assets/img/logo-mfl.png"
                                            alt=""
                                        />
                                    </div>
                                )
                            ) : (
                                <div className="w-full min-h-[50vh] flex-center">
                                    <div className="loading loading-bars" />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="h-12 md:hidden" />
                </main>
                <footer className="fixed bottom-0 left-0 w-full bg-white text-black z-[999]">
                    <div className="w-full px-4 py-2 flex flex-row justify-between items-center">
                        <div className="overflow-hidden">
                            <LanguageSwitcher className="pr-4" />
                        </div>
                        <div className="overflow-hidden">
                            <Link to={ABOUT_PATH}>
                                <p className="about-us uppercase font-bold ">
                                    {t("landing.aboutUs")}
                                </p>
                            </Link>
                        </div>
                        <div className="flex-1" />
                        <div className="flex flex-row gap-6">
                            <div className="overflow-hidden">
                                <SocialMediaLink
                                    className={"social-media-link"}
                                    iconPath={"/assets/icon/facebook.svg"}
                                    label="Facebook"
                                    href={
                                        "https://www.facebook.com/MalaysiaFightLeague/"
                                    }
                                />
                            </div>
                            <div className="overflow-hidden">
                                <SocialMediaLink
                                    className={"social-media-link"}
                                    iconPath={"/assets/icon/instagram.svg"}
                                    label="Instagram"
                                    href={
                                        "https://www.instagram.com/malaysiafightleague/"
                                    }
                                />
                            </div>
                            <div className="overflow-hidden">
                                <SocialMediaLink
                                    className={"social-media-link"}
                                    iconPath={"/assets/icon/tiktok.svg"}
                                    label="TikTok"
                                    href={
                                        "https://www.tiktok.com/@malaysiafightleague?_t=8avi41ssbah&_r=1"
                                    }
                                />
                            </div>
                            <div className="overflow-hidden">
                                <SocialMediaLink
                                    className={"social-media-link"}
                                    iconPath={"/assets/icon/youtube.svg"}
                                    label="YouTube"
                                    href={
                                        "https://youtube.com/@malaysiafightleague"
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </footer>
            </div>
        </>
    );
}
